import type { Bounds2D } from '@/utils/bounds';
import type {
  IConnectionModel,
  ITileManagerModel,
  IChipInstanceModel,
} from '@/interfaces/models';
import type { InterfaceTileState } from '@/schemas/tiles';
import type { InterfaceLinkInstanceId } from '@/types/ids';
import type { TileId } from '@/types/ids';
import { deepClone } from '@/utils/clone';
import {
  type ClipboardData,
  type ClipboardConnection,
  type ClipboardEdgeHint,
  makeClipChipRef,
  type ClipboardChipInstance,
  type ClipboardTileEntry,
  serializeClipboardData,
  isClipboardDataString,
  CLIPBOARD_DATA_PREFIX,
} from '@/models/clipboard/ClipboardData';
import { ClipboardDataSchema } from '@/schemas/clipboard';
import type { Side } from '@/schemas/common';

export function copySelection(
  selectionBounds: Bounds2D,
  tileManager: ITileManagerModel,
  connectionModel: IConnectionModel,
  circuitChips: IChipInstanceModel[],
): ClipboardData | null {
  const tilesInBounds = tileManager.inRange(selectionBounds);
  if (tilesInBounds.size === 0) return null;

  const tileIdSet = new Set<TileId>();
  for (const [id] of tilesInBounds) {
    tileIdSet.add(id);
  }

  // Determine fully-enclosed chip instances
  const chipInstanceTileCounts = new Map<
    string,
    { total: number; inSelection: number }
  >();
  for (const tile of tileManager.tiles) {
    const chipId = tile.meta.partOfChip;
    if (!chipId) continue;
    const entry = chipInstanceTileCounts.get(chipId) ?? { total: 0, inSelection: 0 };
    entry.total++;
    if (tileIdSet.has(tile.id)) {
      entry.inSelection++;
    }
    chipInstanceTileCounts.set(chipId, entry);
  }

  const fullyEnclosedChipIds = new Set<string>();
  for (const [chipInstanceId, counts] of chipInstanceTileCounts) {
    if (counts.total === counts.inSelection) {
      fullyEnclosedChipIds.add(chipInstanceId);
    }
  }

  // Build chip instance index
  const chipInstanceIndexMap = new Map<string, number>();
  const clipboardChipInstances: ClipboardChipInstance[] = [];
  for (const chip of circuitChips) {
    if (fullyEnclosedChipIds.has(chip.id)) {
      const idx = clipboardChipInstances.length;
      chipInstanceIndexMap.set(chip.id, idx);
      clipboardChipInstances.push({
        bundleWidth: chip.bundleWidth,
        chipId: chip.chipId,
        rotation: chip.rotation,
        relativeOrigin: {
          x: chip.originPoint.x - selectionBounds.left,
          y: chip.originPoint.y - selectionBounds.top,
        },
        localIndex: idx,
      });
    }
  }

  // Build tile entries, filtering out partially-selected chip tiles
  const tiles: ClipboardTileEntry[] = [];
  const includedTileIds = new Set<TileId>();
  const tileLocationMap = new Map<string, TileId>();

  for (const [tileId, tile] of tilesInBounds) {
    const chipId = tile.meta.partOfChip;
    if (chipId && !fullyEnclosedChipIds.has(chipId)) {
      continue; // skip partially-selected chip tiles
    }

    const relX = tile.location.x - selectionBounds.left;
    const relY = tile.location.y - selectionBounds.top;

    const meta = deepClone(tile.meta);
    // Map partOfChip to local index string or null
    if (meta.partOfChip && chipInstanceIndexMap.has(meta.partOfChip)) {
      meta.partOfChip = makeClipChipRef(
        chipInstanceIndexMap.get(meta.partOfChip)!,
      ) as any;
    } else {
      meta.partOfChip = null;
    }

    const linkId = (tile.state as InterfaceTileState<unknown> | undefined)
      ?.connectedInterface?.interfaceLinkId;
    const link =
      !tile.meta.partOfChip && linkId
        ? tile.context?.editor?.currentChipEditor?.chipModel.interfaceLinkMap?.get(
            linkId as InterfaceLinkInstanceId,
          )
        : undefined;
    tiles.push({
      sourceTileId: tile.id,
      tileType: tile.tileType,
      relativeLocation: { x: relX, y: relY },
      meta,
      interfaceLink: link ? deepClone(link.dto) : undefined,
      state: tile.state !== undefined ? deepClone(tile.state) : undefined,
    });

    includedTileIds.add(tileId);
    tileLocationMap.set(`${tile.location.x},${tile.location.y}`, tileId);
  }

  if (tiles.length === 0) return null;

  // Build tile ID → relative position map
  const tileIdToRelPos = new Map<TileId, { x: number; y: number }>();
  for (const [tileId, tile] of tilesInBounds) {
    if (includedTileIds.has(tileId)) {
      tileIdToRelPos.set(tileId, {
        x: tile.location.x - selectionBounds.left,
        y: tile.location.y - selectionBounds.top,
      });
    }
  }

  // Capture connections and edge hints
  const connections: ClipboardConnection[] = [];
  const edgeHints: ClipboardEdgeHint[] = [];
  const connectionsSeen = new Set<string>();

  for (const tileId of includedTileIds) {
    const tileConnections = connectionModel.connectionsFor(tileId);
    const relPos = tileIdToRelPos.get(tileId);
    if (!relPos) continue;

    for (const [side, target] of tileConnections) {
      if (includedTileIds.has(target.tileId)) {
        // Internal connection
        const targetRelPos = tileIdToRelPos.get(target.tileId);
        if (!targetRelPos) continue;

        const key = makeConnectionKey(
          relPos.x,
          relPos.y,
          side,
          targetRelPos.x,
          targetRelPos.y,
          target.side,
        );
        if (connectionsSeen.has(key)) continue;
        connectionsSeen.add(key);

        connections.push({
          a: { x: relPos.x, y: relPos.y, side },
          b: { x: targetRelPos.x, y: targetRelPos.y, side: target.side },
        });
      } else {
        // Edge hint — connection goes outside selection
        edgeHints.push({
          relativeLocation: { x: relPos.x, y: relPos.y },
          side,
        });
      }
    }
  }

  const disconnected: ClipboardConnection[] = [];
  for (const edge of connectionModel.dto?.disconnected ?? []) {
    const a = tileIdToRelPos.get(edge.a.tileId);
    const b = tileIdToRelPos.get(edge.b.tileId);
    if (a && b)
      disconnected.push({
        a: { ...a, side: edge.a.side },
        b: { ...b, side: edge.b.side },
      });
  }

  const width = selectionBounds.right - selectionBounds.left + 1;
  const height = selectionBounds.bottom - selectionBounds.top + 1;

  return {
    version: 1,
    width,
    height,
    tiles,
    connections,
    ...(disconnected.length ? { disconnected } : {}),
    edgeHints,
    chipInstances: clipboardChipInstances,
  };
}

function makeConnectionKey(
  ax: number,
  ay: number,
  aSide: Side,
  bx: number,
  by: number,
  bSide: Side,
): string {
  const keyA = `${ax},${ay}:${aSide}`;
  const keyB = `${bx},${by}:${bSide}`;
  return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
}

export async function writeToClipboard(data: ClipboardData): Promise<void> {
  const text = serializeClipboardData(data);
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard API denied — caller should store in-memory fallback
    throw new ClipboardAccessError('Failed to write to clipboard');
  }
}

export async function readFromClipboard(): Promise<ClipboardData | null> {
  try {
    const text = await navigator.clipboard.readText();
    return parseClipboardText(text);
  } catch {
    throw new ClipboardAccessError('Failed to read from clipboard');
  }
}

export function parseClipboardText(text: string): ClipboardData | null {
  if (!isClipboardDataString(text)) return null;
  try {
    const json = JSON.parse(text.slice(CLIPBOARD_DATA_PREFIX.length));
    const result = ClipboardDataSchema.safeParse(json);
    if (!result.success) return null;
    return result.data as ClipboardData;
  } catch {
    return null;
  }
}

export class ClipboardAccessError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClipboardAccessError';
  }
}
